import { InteractionStatus } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { useEffect } from 'react'
import logger from '../../app/middleware/log'
import { loginRequest } from '../../authConfig'
import { haltForInteractiveReauth, isInteractionRequiredError } from './interactiveReauth'

// Proactive token preflight for long-idle tabs (the Friday-tab-on-Monday case). Entra caps a SPA's
// refresh token at 24 hours, so a tab left open over a weekend ALWAYS comes back with silent renewal
// dead — without this, the user's first action fails and THEN redirects. Checking when the tab becomes
// visible again means the re-auth redirect (usually a promptless SSO bounce, returning here via the
// MSAL `state` deep link) happens before the user clicks anything. acquireTokenSilent resolves from
// cache while the token is fresh, so ordinary tab switches cost nothing.
const IdleSessionPreflight = (): null => {
  const { instance, accounts, inProgress } = useMsal()

  useEffect(() => {
    // Mid-interaction (redirect processing, login in flight) a preflight would just collide with it.
    if (inProgress !== InteractionStatus.None) {
      return undefined
    }

    const onVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible') {
        return
      }
      // Same active-account resolution as App's sign-in bootstrap; no account (signed out, /login tab)
      // means there is no session to preflight.
      const account = instance.getActiveAccount() ?? accounts[0] ?? null
      if (!account) {
        return
      }
      instance.acquireTokenSilent({ ...loginRequest, account }).catch((error: unknown) => {
        if (isInteractionRequiredError(error)) {
          // Rejection = the redirect failed to launch (already logged inside); the per-request
          // interceptor remains as the fallback on the user's next action.
          haltForInteractiveReauth(instance, account).catch(() => undefined)
        } else {
          // Not an auth dead-end (network blip etc.) — leave it to the per-request path.
          logger.warn('Idle-session token preflight failed with a non-interactive error', error)
        }
      })
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [instance, accounts, inProgress])

  return null
}

export default IdleSessionPreflight
